import { createClient, type SupabaseClient } from "@supabase/supabase-js";

import type { OAuthProvider } from "../shared/api-contract";
import {
  OAuthCallbackMismatchError,
  PendingContributionStore,
  PendingIntentExpiredError,
  type PendingContribution,
} from "./pending-contribution";

export const AUTH_CALLBACK_PATH = "/auth/callback";
export const CALLBACK_ATTEMPT_STORAGE_KEY = "the-shit-list.oauth-attempt.v1";
export const STANDALONE_LOGIN_STORAGE_KEY = "the-shit-list.login-attempt.v1";
export const LEGACY_CONTRIBUTION_STORAGE_KEY = "the-shit-list.contribution.v1";

export function confirmationActionLabel(
  signedIn: boolean,
): "Verify and cast" | "Sign in and cast" {
  return signedIn ? "Verify and cast" : "Sign in and cast";
}

export function removeLegacyContribution(storage: Pick<Storage, "removeItem">): void {
  storage.removeItem(LEGACY_CONTRIBUTION_STORAGE_KEY);
}

export interface BrowserAuthConfig {
  url: string;
  publishableKey: string;
}

interface AuthClientOptions {
  auth: {
    autoRefreshToken: true;
    detectSessionInUrl: false;
    flowType: "pkce";
    persistSession: true;
  };
}

type AuthClientFactory<T> = (url: string, key: string, options: AuthClientOptions) => T;

export function readBrowserAuthConfig(
  environment: Record<string, string | boolean | undefined>,
): BrowserAuthConfig {
  const rawUrl = environment.VITE_SUPABASE_URL;
  const publishableKey = environment.VITE_SUPABASE_PUBLISHABLE_KEY;
  if (typeof rawUrl !== "string" || typeof publishableKey !== "string") {
    throw new Error("Supabase public browser configuration is missing.");
  }
  const url = new URL(rawUrl);
  const local = url.hostname === "localhost" || url.hostname === "127.0.0.1";
  if (url.protocol !== "https:" && !(local && url.protocol === "http:")) {
    throw new Error("Supabase browser URL must use HTTPS outside local development.");
  }
  if (!publishableKey.startsWith("sb_publishable_") || publishableKey.includes("replace_me")) {
    throw new Error("Supabase browser auth requires a publishable key.");
  }
  return { url: url.origin, publishableKey };
}

export function createBrowserAuthClient(config: BrowserAuthConfig): SupabaseClient;
export function createBrowserAuthClient<T>(config: BrowserAuthConfig, factory: AuthClientFactory<T>): T;
export function createBrowserAuthClient<T>(
  config: BrowserAuthConfig,
  factory: AuthClientFactory<T> = createClient as unknown as AuthClientFactory<T>,
): T {
  return factory(config.url, config.publishableKey, {
    auth: {
      autoRefreshToken: true,
      detectSessionInUrl: false,
      flowType: "pkce",
      persistSession: true,
    },
  });
}

export function fixedCallbackUrl(urlOrOrigin: string): string {
  return new URL(AUTH_CALLBACK_PATH, new URL(urlOrOrigin).origin).toString();
}

interface OAuthStartAuth {
  signInWithOAuth(input: {
    provider: OAuthProvider;
    options: { redirectTo: string };
  }): Promise<{ data: unknown; error: { message?: string } | null }>;
}

export async function launchOAuth(options: {
  auth: OAuthStartAuth;
  provider: OAuthProvider;
  intentId: string;
  ownerId: string;
  origin: string;
  storage: Storage;
  pending: PendingContributionStore;
}): Promise<void> {
  options.storage.removeItem(STANDALONE_LOGIN_STORAGE_KEY);
  const lease = await options.pending.acquireOAuthLease(
    options.intentId,
    options.provider,
    options.ownerId,
  );
  options.storage.setItem(CALLBACK_ATTEMPT_STORAGE_KEY, lease.attemptId);
  const { error } = await options.auth.signInWithOAuth({
    provider: options.provider,
    options: { redirectTo: fixedCallbackUrl(options.origin) },
  });
  if (error) {
    options.storage.removeItem(CALLBACK_ATTEMPT_STORAGE_KEY);
    await options.pending.markRetryable(options.intentId, "provider_start_failed");
    throw new Error("Sign-in could not be started. Try again.");
  }
}

export async function launchStandaloneOAuth(options: {
  auth: OAuthStartAuth;
  provider: OAuthProvider;
  origin: string;
  storage: Storage;
}): Promise<void> {
  options.storage.removeItem(CALLBACK_ATTEMPT_STORAGE_KEY);
  options.storage.setItem(STANDALONE_LOGIN_STORAGE_KEY, options.provider);
  const { error } = await options.auth.signInWithOAuth({
    provider: options.provider,
    options: { redirectTo: fixedCallbackUrl(options.origin) },
  });
  if (error) {
    options.storage.removeItem(STANDALONE_LOGIN_STORAGE_KEY);
    throw new Error("Login could not be started. Try again.");
  }
}

interface OAuthExchangeAuth {
  exchangeCodeForSession(code: string): Promise<{
    data: { session?: unknown };
    error: { message?: string } | null;
  }>;
}

interface CallbackHistory {
  state: unknown;
  replaceState(data: unknown, unused: string, url?: string | URL | null): void;
}

export type OAuthCallbackResult =
  | { status: "idle" }
  | { status: "standalone_complete"; session: unknown }
  | { status: "standalone_error"; message: string }
  | { status: "completing"; intent: PendingContribution; session: unknown }
  | {
      status: "retryable_error" | "expired" | "canceled";
      intent?: PendingContribution;
      message: string;
    };

export async function handleOAuthCallback(options: {
  auth: OAuthExchangeAuth;
  href: string;
  history: CallbackHistory;
  storage: Storage;
  pending: PendingContributionStore;
}): Promise<OAuthCallbackResult> {
  const callbackUrl = new URL(options.href);
  if (callbackUrl.pathname !== AUTH_CALLBACK_PATH) return { status: "idle" };

  const code = callbackUrl.searchParams.get("code");
  const providerError = callbackUrl.searchParams.get("error");
  const hasCallbackValues = callbackUrl.search.length > 0 || callbackUrl.hash.length > 0;
  if (!hasCallbackValues) return { status: "idle" };

  const attemptId = options.storage.getItem(CALLBACK_ATTEMPT_STORAGE_KEY);
  const rawStandaloneProvider = options.storage.getItem(STANDALONE_LOGIN_STORAGE_KEY);
  const standaloneProvider = rawStandaloneProvider === "google" ? rawStandaloneProvider : null;
  options.storage.removeItem(CALLBACK_ATTEMPT_STORAGE_KEY);
  options.storage.removeItem(STANDALONE_LOGIN_STORAGE_KEY);
  options.history.replaceState(options.history.state, "", AUTH_CALLBACK_PATH);

  if (!attemptId && standaloneProvider) {
    if (providerError) {
      return {
        status: "standalone_error",
        message: "Login was canceled or denied. Try again.",
      };
    }
    if (!code) {
      return {
        status: "standalone_error",
        message: "This login callback could not be completed. Try again.",
      };
    }
    const { data, error } = await options.auth.exchangeCodeForSession(code);
    return error
      ? { status: "standalone_error", message: "Login could not be completed. Try again." }
      : { status: "standalone_complete", session: data.session };
  }

  const activeLease = await options.pending.getActiveLease();
  const intent = activeLease ? await options.pending.get(activeLease.intentId) : undefined;
  if (providerError) {
    if (attemptId && activeLease?.attemptId === attemptId) {
      await options.pending.markRetryable(activeLease.intentId, "provider_denied");
    }
    return {
      status: "retryable_error",
      intent,
      message: "Sign-in was canceled or denied. Try again or cancel this contribution.",
    };
  }
  if (!code || !attemptId) {
    return {
      status: "retryable_error",
      intent,
      message: "This sign-in callback cannot be matched. Try again or cancel.",
    };
  }

  let claimed: Awaited<ReturnType<PendingContributionStore["beginOAuthCallback"]>>;
  try {
    claimed = await options.pending.beginOAuthCallback(attemptId);
  } catch (error) {
    if (error instanceof PendingIntentExpiredError) {
      return { status: "expired", intent, message: "This confirmed contribution expired. Try again or cancel." };
    }
    if (error instanceof OAuthCallbackMismatchError) {
      return {
        status: "retryable_error",
        intent,
        message: "This sign-in callback does not match the active contribution. Try again or cancel.",
      };
    }
    throw error;
  }

  const { data, error } = await options.auth.exchangeCodeForSession(code);
  if (error) {
    await options.pending.markRetryable(claimed.intent.intentId, "code_exchange_failed");
    return {
      status: "retryable_error",
      intent: claimed.intent,
      message: "Sign-in could not be completed. Try again or cancel this contribution.",
    };
  }

  const completing = await options.pending.markCompleting(
    claimed.intent.intentId,
    claimed.lease.attemptId,
  );
  return { status: "completing", intent: completing, session: data.session };
}

export interface AccountMenuController {
  destroy(): void;
  open(): void;
}

export function mountAccountMenu(
  host: HTMLElement,
  options: {
    signedIn: boolean;
    loginError?: string | null;
    onLogin: (selectedButton: HTMLButtonElement) => void | Promise<void>;
    onLogout: () => void | Promise<void>;
  },
): AccountMenuController {
  host.replaceChildren();
  if (!options.signedIn) {
    const details = document.createElement("details");
    details.className = "account-disclosure account-disclosure--login";
    const summary = document.createElement("summary");
    summary.className = "account-summary account-login";
    summary.textContent = "Log in";
    summary.setAttribute("aria-label", "Log in");
    const panel = document.createElement("div");
    panel.className = "account-menu account-menu--login";
    panel.setAttribute("role", "group");
    panel.setAttribute("aria-label", "Login options");
    const copy = document.createElement("p");
    copy.className = "account-menu__copy";
    copy.textContent = "Sign in to bind one permanent contribution to this account.";
    panel.append(copy);
    if (options.loginError) {
      const error = document.createElement("p");
      error.className = "account-menu__error";
      error.setAttribute("role", "alert");
      error.textContent = options.loginError;
      panel.append(error);
    }
    const login = document.createElement("button");
    login.type = "button";
    login.className = "button button--primary account-menu__login-action";
    login.textContent = "Continue with Google";
    panel.append(login);
    details.append(summary, panel);
    host.append(details);

    const closeAndRestoreFocus = () => {
      details.open = false;
      summary.focus();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || !details.open) return;
      event.preventDefault();
      closeAndRestoreFocus();
    };
    const onPointerDown = (event: PointerEvent) => {
      if (details.open && event.target instanceof Node && !details.contains(event.target)) {
        details.open = false;
      }
    };
    const onLoginClick = async () => {
      login.disabled = true;
      try {
        await options.onLogin(login);
      } finally {
        if (login.isConnected) login.disabled = false;
      }
    };

    details.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    login.addEventListener("click", onLoginClick);
    return {
      destroy() {
        details.removeEventListener("keydown", onKeyDown);
        document.removeEventListener("pointerdown", onPointerDown);
        login.removeEventListener("click", onLoginClick);
        host.replaceChildren();
      },
      open() {
        details.open = true;
      },
    };
  }

  const details = document.createElement("details");
  details.className = "account-disclosure";
  const summary = document.createElement("summary");
  summary.className = "account-summary";
  summary.textContent = "Account";
  summary.setAttribute("aria-label", "Account menu");
  const menu = document.createElement("div");
  menu.className = "account-menu";
  menu.setAttribute("role", "menu");
  const logout = document.createElement("button");
  logout.type = "button";
  logout.className = "account-menu__action";
  logout.setAttribute("role", "menuitem");
  logout.textContent = "Log out";
  menu.append(logout);
  details.append(summary, menu);
  host.append(details);

  const closeAndRestoreFocus = () => {
    details.open = false;
    summary.focus();
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== "Escape" || !details.open) return;
    event.preventDefault();
    closeAndRestoreFocus();
  };
  const onPointerDown = (event: PointerEvent) => {
    if (details.open && event.target instanceof Node && !details.contains(event.target)) {
      closeAndRestoreFocus();
    }
  };
  const onLogoutClick = async () => {
    logout.disabled = true;
    try {
      await options.onLogout();
    } finally {
      if (logout.isConnected) logout.disabled = false;
    }
  };

  details.addEventListener("keydown", onKeyDown);
  document.addEventListener("pointerdown", onPointerDown);
  logout.addEventListener("click", onLogoutClick);

  return {
    destroy() {
      details.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
      logout.removeEventListener("click", onLogoutClick);
      host.replaceChildren();
    },
    open() {
      details.open = true;
    },
  };
}
